import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Close as CloseIcon,
  Category as CategoryIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import { getFeaturesCategory, saveSubCategories } from 'src/ApiServices';
import useMediaQuery from '@mui/material/useMediaQuery';

export default function AddEditSubCategoryModal({ open, onClose, onSuccess, category }) {
  const [loading, setLoading] = useState(false);
  const [mainCategories, setMainCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [formData, setFormData] = useState({
    parent_id: ''
  });

  const [subCategories, setSubCategories] = useState([{ id: 1, name: '' }]);

  useEffect(() => {
    if (open) fetchMainCategories();
  }, [open]);

  useEffect(() => {
    if (category) {
      setFormData({
        parent_id: category.parent_id || category.feature_category_id || category.id || ''
      });

      if (category.sub_category && category.sub_category.length > 0) {
        setSubCategories(
          category.sub_category.map((sub) => ({
            id: sub.id,
            name: sub.name
          }))
        );
      } else {
        setSubCategories([{ id: 1, name: '' }]);
      }
    } else {
      setFormData({ parent_id: '' });
      setSubCategories([{ id: 1, name: '' }]);
    }
  }, [category, open]);

  const fetchMainCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await getFeaturesCategory();
      if (response?.data) setMainCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch main categories:', error);
      toast.error('Failed to load main categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubCategoryChange = (id, value) => {
    setSubCategories((prev) => prev.map((sub) => (sub.id === id ? { ...sub, name: value } : sub)));
  };

  const addSubCategory = () => {
    const newId = subCategories.length > 0 ? Math.max(...subCategories.map((s) => s.id)) + 1 : 1;
    setSubCategories((prev) => [...prev, { id: newId, name: '' }]);
  };

  const deleteSubCategory = (id) => {
    if (subCategories.length <= 1) {
      toast.error('At least one sub-category is required');
      return;
    }

    if (category && id === 1 && subCategories[0].name.trim() !== '') {
      toast.error('Cannot delete the first sub-category when editing existing data');
      return;
    }

    setSubCategories((prev) => prev.filter((sub) => sub.id !== id));
  };

  const handleSubmit = async () => {
    if (!formData.parent_id) {
      toast.error('Please select a main category');
      return;
    }

    const validSubCategories = subCategories.filter((sub) => sub.name.trim() !== '');
    if (validSubCategories.length === 0) {
      toast.error('Please add at least one sub-category');
      return;
    }

    const subCategoryObjects = [];
    for (let i = 0; i < validSubCategories.length; i++) {
      const name = validSubCategories[i].name.trim();
      if (!name) {
        toast.error(`Please enter a name for sub-category ${i + 1}`);
        return;
      }
      subCategoryObjects.push({ name });
    }

    const uniqueNames = new Set(subCategoryObjects.map((obj) => obj.name.toLowerCase()));
    if (uniqueNames.size !== subCategoryObjects.length) {
      toast.error('Duplicate sub-category names are not allowed');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        feature_category_id: formData.parent_id,
        sub_categories: JSON.stringify(subCategoryObjects)
      };

      // console.log('Payload being sent:', payload);
      // console.log('sub_categories JSON:', payload.sub_categories);

      await saveSubCategories(payload);

      toast.success('Sub categories saved successfully!');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to save sub-categories:', error);

      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Full error:', error.response);
      }

      toast.error(error.response?.data?.message || 'Failed to save sub-categories!');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      parent_id: ''
    });
    setSubCategories([{ id: 1, name: '' }]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : { xs: 2, sm: 3 },
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '900px'
        }
      }}
    >
      <DialogTitle
        sx={{
          py: 3,
          px: 3,
          pb: 2,
          backgroundColor: palette.primary.main,
          color: 'white'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CategoryIcon />
            <Typography variant="h6" sx={{ fontFamily: 'poppinsSemiBold', color: 'white' }}>
              {category ? 'Edit Sub Categories' : 'Add Sub Categories'}
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3, mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl fullWidth size="medium" sx={{ mt: 1 }}>
              <InputLabel id="main-category-label">Main Category *</InputLabel>
              <Select
                name="parent_id"
                value={formData.parent_id}
                onChange={handleChange}
                label="Main Category *"
                labelId="main-category-label"
                disabled={categoriesLoading}
                required
              >
                <MenuItem value="">Select Main Category</MenuItem>
                {mainCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Add Another Sub Category">
              <IconButton
                onClick={addSubCategory}
                sx={{
                  bgcolor: palette.primary.main,
                  color: 'white',
                  '&:hover': { bgcolor: palette.primary.dark }
                }}
                size="small"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontFamily: 'poppinsMedium' }}>
                Sub Categories: *
              </Typography>
            </Box>

            {subCategories.map((subCategory, index) => (
              <Box
                key={subCategory.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <TextField
                  fullWidth
                  label={`Sub Category ${index + 1}`}
                  value={subCategory.name}
                  onChange={(e) => handleSubCategoryChange(subCategory.id, e.target.value)}
                  placeholder="Enter sub category name"
                  size="medium"
                  required={index === 0}
                />

                {subCategories.length > 1 && (
                  <Tooltip title="Delete Sub Category">
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => deleteSubCategory(subCategory.id)}
                        disabled={
                          category && subCategory.id === 1 && subCategories[0].name.trim() !== ''
                        }
                        sx={{
                          bgcolor: '#d32f2f',
                          color: 'white',
                          '&:hover': { bgcolor: '#b71c1c' },
                          '&.Mui-disabled': {
                            bgcolor: '#f5f5f5',
                            color: '#bdbdbd'
                          }
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ py: 3, px: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: palette.grey[400],
            color: palette.text.primary,
            minWidth: 100,
            fontFamily: 'poppinsMedium',
            px: 3
          }}
          disabled={loading}
        >
          Cancel
        </Button>

        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={loading}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            minWidth: 100,
            fontFamily: 'poppinsMedium',
            px: 3,
            '&:hover': { backgroundColor: palette.primary.dark }
          }}
        >
          {category ? 'Update' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
